import React, { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { User } from 'firebase/auth';
import { useAuth } from '../hooks/useAuth';
import { useUserAccess } from '../context/UserAccessContext';
import { LoadingSpinner } from './LoadingSpinner';
import { AccessDenied } from './AccessDenied';

export interface ProtectedRouteContext {
  authUser: User;
  isSuperAdmin: boolean;
  checkUserAccess: (email: string, menuId: string) => boolean;
}

export interface ProtectedRouteProps {
  children: ReactNode;
  /**
   * Daftar menu ID yang diizinkan. User CUKUP memiliki SALAH SATU (semantik OR)
   * untuk bisa mengakses rute. Super Admin selalu diizinkan.
   * Bila dikosongkan, rute hanya butuh autentikasi (perilaku lama).
   */
  requiredMenuIds?: string[];
  /**
   * Logika otorisasi khusus bila `requiredMenuIds` tidak cukup
   * (mis. cek jenjang/wilayah). Diprioritaskan di atas `requiredMenuIds`.
   */
  customCheck?: (ctx: ProtectedRouteContext) => boolean;
  /** Target redirect saat ditolak. Default dashboard guru `/portal-guru`. */
  redirectTo?: string;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredMenuIds,
  customCheck,
  redirectTo,
}) => {
  const { authUser, loading: authLoading } = useAuth();
  const { isSuperAdmin, checkUserAccess, loading: accessLoading, isAkunLayananNonaktif } = useUserAccess();

  const needsAuthorization = Boolean(
    (requiredMenuIds && requiredMenuIds.length > 0) || customCheck
  );

  if (authLoading) {
    return <LoadingSpinner />;
  }

  if (!authUser) {
    return <Navigate to="/" replace />;
  }

  // Akun layanan yang dinonaktifkan (docs/PANDUAN_AKUN_LAYANAN.md).
  if (isAkunLayananNonaktif) {
    return <Navigate to="/nonaktif" replace />;
  }

  // Perilaku lama: tanpa persyaratan otorisasi → cukup login.
  if (!needsAuthorization) {
    return <>{children}</>;
  }

  if (accessLoading) {
    return <LoadingSpinner />;
  }

  // Super Admin selalu diizinkan (bypass).
  if (isSuperAdmin) {
    return <>{children}</>;
  }

  let allowed = false;

  if (customCheck) {
    allowed = Boolean(customCheck({ authUser, isSuperAdmin, checkUserAccess }));
  } else if (requiredMenuIds && requiredMenuIds.length > 0) {
    const email = authUser.email || '';
    allowed = requiredMenuIds.some((menuId) => checkUserAccess(email, menuId));
  }

  if (!allowed) {
    return <AccessDenied redirectTo={redirectTo} />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
